/**
 * Shared node label + AppBar title logic for `/audit-trail/item_type_cat_update_preview/…`
 * and matching `/audit-trail/review/…` chrome for UPDATE_STEP / UPDATE_CANVAS (full-tree audits).
 */

const CHILD_LABEL_MAP = {
  attachment: "Attachment",
  video: "Video",
  "rich-text": "Rich Text",
  canva: "Canvas",
};

function countChildrenOfTypeBefore(
  sections,
  targetSIdx,
  targetStIdx,
  targetSubIdx,
  targetChIdx,
  childType,
) {
  let count = 0;
  for (let sIdx = 0; sIdx < sections.length; sIdx++) {
    const steps = sections[sIdx]?.steps ?? [];
    for (let stIdx = 0; stIdx < steps.length; stIdx++) {
      const step = steps[stIdx];
      const stepCh = (step?.children ?? []).filter((c) => c?.type === childType);
      if (sIdx === targetSIdx && stIdx === targetStIdx && targetSubIdx < 0) {
        const allCh = step?.children ?? [];
        for (let i = 0; i < targetChIdx; i++) {
          if (allCh[i]?.type === childType) count++;
        }
        return count;
      }
      count += stepCh.length;

      const substeps = step?.substeps ?? [];
      for (let subIdx = 0; subIdx < substeps.length; subIdx++) {
        const subCh = (substeps[subIdx]?.children ?? []).filter(
          (c) => c?.type === childType,
        );
        if (sIdx === targetSIdx && stIdx === targetStIdx && subIdx === targetSubIdx) {
          const allSubCh = substeps[subIdx]?.children ?? [];
          for (let i = 0; i < targetChIdx; i++) {
            if (allSubCh[i]?.type === childType) count++;
          }
          return count;
        }
        count += subCh.length;
      }
    }
  }
  return count;
}

/**
 * Resolve section / step / substep / leaf by `tree_node_id` — same rules as `useItemCatTypeUpdate`.
 */
export function resolveItemCatPreviewNode(sections, targetTreeNodeId) {
  const empty = {
    type: null,
    sectionIndex: -1,
    section: null,
    stepIndex: -1,
    substepIndex: -1,
    childIndex: -1,
    childNode: null,
    globalStepStart: 0,
    globalChildNumber: 0,
  };

  if (!targetTreeNodeId || !Array.isArray(sections) || sections.length === 0) {
    return empty;
  }

  let cumSteps = 0;
  for (let sIdx = 0; sIdx < sections.length; sIdx++) {
    const sec = sections[sIdx];

    if (sec?.tree_node_id === targetTreeNodeId) {
      return {
        ...empty,
        type: "section",
        sectionIndex: sIdx,
        section: sec,
        globalStepStart: cumSteps,
      };
    }

    const steps = sec?.steps ?? [];
    for (let stIdx = 0; stIdx < steps.length; stIdx++) {
      const step = steps[stIdx];

      if (step?.tree_node_id === targetTreeNodeId) {
        return {
          ...empty,
          type: "step",
          sectionIndex: sIdx,
          section: sec,
          stepIndex: stIdx,
          globalStepStart: cumSteps,
        };
      }

      const stepChildren = step?.children ?? [];
      for (let chIdx = 0; chIdx < stepChildren.length; chIdx++) {
        if (stepChildren[chIdx]?.tree_node_id === targetTreeNodeId) {
          const childType = stepChildren[chIdx].type;
          const globalChildNumber =
            countChildrenOfTypeBefore(
              sections,
              sIdx,
              stIdx,
              -1,
              chIdx,
              childType,
            ) + 1;
          return {
            ...empty,
            type: childType,
            sectionIndex: sIdx,
            section: sec,
            stepIndex: stIdx,
            childIndex: chIdx,
            childNode: stepChildren[chIdx],
            globalStepStart: cumSteps,
            globalChildNumber,
          };
        }
      }

      const substeps = step?.substeps ?? [];
      for (let subIdx = 0; subIdx < substeps.length; subIdx++) {
        const substep = substeps[subIdx];

        if (substep?.tree_node_id === targetTreeNodeId) {
          return {
            ...empty,
            type: "substep",
            sectionIndex: sIdx,
            section: sec,
            stepIndex: stIdx,
            substepIndex: subIdx,
            globalStepStart: cumSteps,
          };
        }

        const subChildren = substep?.children ?? [];
        for (let chIdx = 0; chIdx < subChildren.length; chIdx++) {
          if (subChildren[chIdx]?.tree_node_id === targetTreeNodeId) {
            const childType = subChildren[chIdx].type;
            const globalChildNumber =
              countChildrenOfTypeBefore(
                sections,
                sIdx,
                stIdx,
                subIdx,
                chIdx,
                childType,
              ) + 1;
            return {
              ...empty,
              type: childType,
              sectionIndex: sIdx,
              section: sec,
              stepIndex: stIdx,
              substepIndex: subIdx,
              childIndex: chIdx,
              childNode: subChildren[chIdx],
              globalStepStart: cumSteps,
              globalChildNumber,
            };
          }
        }
      }
    }
    cumSteps += steps.length;
  }
  return empty;
}

/**
 * AppBar title string — must match `useItemCatTypeUpdate` / PreviewCommonComponent (`… Selected`).
 */
export function buildItemCatPreviewAppBarTitle(versionId, resolved) {
  if (!versionId) return "Loading...";
  const suffix = `(Version ${versionId}) Selected`;
  if (resolved.type === "section" && resolved.sectionIndex >= 0) {
    return `Section ${resolved.sectionIndex + 1} ${suffix}`;
  }
  if (resolved.type === "step" && resolved.stepIndex >= 0) {
    return `Step ${resolved.globalStepStart + resolved.stepIndex + 1} ${suffix}`;
  }
  if (resolved.type === "substep" && resolved.substepIndex >= 0) {
    const stepNum = resolved.globalStepStart + resolved.stepIndex + 1;
    const substepLetter = String.fromCharCode(65 + resolved.substepIndex);
    return `Substep ${stepNum}${substepLetter} ${suffix}`;
  }
  if (CHILD_LABEL_MAP[resolved.type] && resolved.childNode) {
    return `${CHILD_LABEL_MAP[resolved.type]} ${
      resolved.globalChildNumber ? `${resolved.globalChildNumber} ` : ""
    }${suffix}`;
  }
  return `Preview ${suffix}`;
}
